import React, { useState, useEffect } from "react";
import { Form, Input, Label, FormFeedback } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { submitSignupDetails } from "../../../slices/auth/register/thunk";
import { resetRegisterState } from "../../../slices/auth/register/reducer";
import { loginSuccess, userProfileFetchSuccess } from "../../../slices/auth/login/reducer";
import { showSuccessToast, showErrorToast } from "../../../helpers/toast_helper";
import appConfig from "../../../config";
import withRouter from "../../../Components/Common/withRouter";
import DashboardHeader from "../../../Components/Common/DashboardHeader";
import AuthLeftPanel from "../../../Components/Common/AuthLeftPanel";
import AuthFormButton from "../../../Components/Common/AuthFormButton";
import AuthLink from "../../../Components/Common/AuthLink";

const CreateNewAccount = () => {
  const navigate = useNavigate();
  const dispatch: any = useDispatch();
  const [loader, setLoader] = useState<boolean>(false);

  const selectRegisterState = (state: any) => state.Register;
  const selectRegisterData = createSelector(selectRegisterState, (register) => ({
    loading: register.loading,
    successMsg: register.successMsg,
    errorMsg: register.errorMsg,
    signupCompleted: register.signupCompleted,
  }));

  const { loading, successMsg, errorMsg, signupCompleted } = useSelector(selectRegisterData);

  const [phoneData] = useState({
    phoneno: sessionStorage.getItem("phoneNumber") || '',
    dialcode: sessionStorage.getItem("dialcode") || appConfig.country.defaultDialCode,
    countrycode: sessionStorage.getItem("countrycode") || appConfig.country.defaultCountryCode,
  });

  useEffect(() => {
    if (!phoneData.phoneno) {
      navigate("/register");
    }
  }, [phoneData.phoneno, navigate]);

  useEffect(() => {
    if (successMsg && signupCompleted) {
      showSuccessToast("User Created Successfully", "signup-success");
      sessionStorage.removeItem("phoneNumber");
      sessionStorage.removeItem("dialcode");
      sessionStorage.removeItem("countrycode");
      setTimeout(() => {
        navigate("/login");
      }, 1500);
      dispatch(resetRegisterState());
    }
  }, [successMsg, signupCompleted, navigate, dispatch]);

  useEffect(() => {
    if (errorMsg) {
      showErrorToast(errorMsg, "signup-error");
      dispatch(resetRegisterState());
    }
  }, [errorMsg, dispatch]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      first_name: '',
      middle_name: '',
      last_name: '',
      email_address: '',
      dob: '',
      referal_code: '',
      datausageconsent: false,
      iagreereceiveoffers: false,
      language_id: 1,
    },
    validationSchema: Yup.object({
      first_name: Yup.string()
        .required("First name is required")
        .min(2, "First name must be at least 2 characters"),
      last_name: Yup.string()
        .required("Last name is required")
        .min(2, "Last name must be at least 2 characters"),
      email_address: Yup.string()
        .required("Email address is required")
        .email("Please enter a valid email address"),
      dob: Yup.string()
        .required("Date of birth is required")
        .matches(/^\d{2}\/\d{2}\/\d{4}$/, "Date must be in DD/MM/YYYY format"),
      datausageconsent: Yup.boolean()
        .oneOf([true], "You must agree to the data usage consent"),
      iagreereceiveoffers: Yup.boolean(),
      referal_code: Yup.string(),
    }),
    onSubmit: async (values) => {
      setLoader(true);
      try {
        const [day, month, year] = values.dob.split('/');
        const dobFormatted = `${year}-${month}-${day}`;

        const result = await dispatch(submitSignupDetails({
          phone_no: phoneData.phoneno,
          country_code: phoneData.countrycode,
          dial_code: phoneData.dialcode,
          language_id: values.language_id,
          first_name: values.first_name,
          middle_name: values.middle_name || '',
          last_name: values.last_name,
          dob: dobFormatted,
          email_address: values.email_address,
          datausageconsent: values.datausageconsent,
          iagreereceiveoffers: values.iagreereceiveoffers,
          referal_code: values.referal_code || '',
        }));

        if (result.success && result.data?.token && result.data?.refresh_token) {
          const authData = {
            token: result.data.token,
            refresh_token: result.data.refresh_token,
            user: result.data.user || {},
            username: result.data.user?.phone_no || phoneData.phoneno,
            phone_no: result.data.user?.phone_no || phoneData.phoneno,
            email_id: result.data.user?.email_id || values.email_address,
          };
          sessionStorage.setItem("authUser", JSON.stringify(authData));
          sessionStorage.setItem("isLoggedIn", "true");
          dispatch(loginSuccess());
          dispatch(userProfileFetchSuccess({
            username: authData.username,
            phone_no: authData.phone_no,
            email_id: authData.email_id,
          }));
        } else {
          setLoader(false);
        }
      } catch (error: any) {
        console.error("Signup error:", error);
        setLoader(false);
      }
    },
  });

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length >= 2) value = value.slice(0, 2) + '/' + value.slice(2);
    if (value.length >= 5) value = value.slice(0, 5) + '/' + value.slice(5, 9);
    validation.setFieldValue('dob', value);
  };

  document.title = "AMANAT - Create New Account";

  return (
    <div className="auth-container">
      <DashboardHeader />
      <div className="auth-main-content">
        <div className="auth-panel-container">
          <AuthLeftPanel
            title="Register"
            description="Welcome to AMANAT. Register now to experience a safer, smarter way forward."
          />
          <div className="auth-right-panel auth-form-scroll">
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                validation.handleSubmit();
                return false;
              }}
              className="auth-form"
            >
              <h2 className="auth-form-title">Create New Account</h2>

              <div className="auth-form-row">
                <div className="auth-form-group">
                  <Label htmlFor="first_name" className="auth-form-label">First name</Label>
                  <Input
                    name="first_name"
                    className="auth-form-input"
                    placeholder="Enter first name"
                    type="text"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.first_name}
                    invalid={validation.touched.first_name && !!validation.errors.first_name}
                  />
                  {validation.touched.first_name && validation.errors.first_name && (
                    <FormFeedback type="invalid">{validation.errors.first_name}</FormFeedback>
                  )}
                </div>
                <div className="auth-form-group">
                  <Label htmlFor="last_name" className="auth-form-label">Last name</Label>
                  <Input
                    name="last_name"
                    className="auth-form-input"
                    placeholder="Enter last name"
                    type="text"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.last_name}
                    invalid={validation.touched.last_name && !!validation.errors.last_name}
                  />
                  {validation.touched.last_name && validation.errors.last_name && (
                    <FormFeedback type="invalid">{validation.errors.last_name}</FormFeedback>
                  )}
                </div>
              </div>

              <div className="auth-form-group">
                <Label htmlFor="email_address" className="auth-form-label">Email Address</Label>
                <Input
                  name="email_address"
                  className="auth-form-input"
                  placeholder="Enter email address"
                  type="email"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.email_address}
                  invalid={validation.touched.email_address && !!validation.errors.email_address}
                />
                {validation.touched.email_address && validation.errors.email_address && (
                  <FormFeedback type="invalid">{validation.errors.email_address}</FormFeedback>
                )}
              </div>

              <div className="auth-form-group">
                <Label htmlFor="dob" className="auth-form-label">Date of Birth</Label>
                <Input
                  name="dob"
                  className="auth-form-input"
                  placeholder="DD/MM/YYYY"
                  type="text"
                  onChange={handleDateChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.dob}
                  invalid={validation.touched.dob && !!validation.errors.dob}
                  maxLength={10}
                />
                {validation.touched.dob && validation.errors.dob && (
                  <FormFeedback type="invalid">{validation.errors.dob}</FormFeedback>
                )}
              </div>

              <div className="auth-form-group">
                <Label htmlFor="referal_code" className="auth-form-label">
                  Have a referral code? (optional)
                </Label>
                <Input
                  name="referal_code"
                  className="auth-form-input"
                  placeholder="Enter referral code"
                  type="text"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.referal_code}
                />
              </div>

              <div className="auth-checkbox-group">
                <div className="checkbox-item">
                  <input
                    type="checkbox"
                    name="iagreereceiveoffers"
                    checked={validation.values.iagreereceiveoffers}
                    onChange={validation.handleChange}
                  />
                  <Label>
                    I agree to use my contact details to receive offers and information.
                  </Label>
                </div>
                <div className="checkbox-item">
                  <input
                    type="checkbox"
                    name="datausageconsent"
                    checked={validation.values.datausageconsent}
                    onChange={validation.handleChange}
                  />
                  <Label>
                    By registering for an Amanat App account, I confirm that I have read, understood and accepted the purposes for which and service providers may collect, store, use, process, update, disclose and transfer my personal data. I hereby provide my explicit consent for such processing of my personal data for KYC and other regulatory purposes, in accordance with the Kingdom of Bahrain's Personal Data Protection Law and the regulation of the Central Bank of Bahrain. Please read the complete consent here.
                  </Label>
                </div>
                {validation.touched.datausageconsent && validation.errors.datausageconsent && (
                  <div className="checkbox-error">{validation.errors.datausageconsent}</div>
                )}
              </div>

              <div className="auth-form-group">
                <AuthFormButton loading={loader || loading} type="submit">
                  Continue
                </AuthFormButton>
              </div>
            </Form>
            <AuthLink to="/login" text="Already have an account?" linkText="Login here" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default withRouter(CreateNewAccount);

